<template>
  <div class="goal-detail-container">
    <div class="page-header">
      <button @click="goBack" class="btn-back">←</button>
      <h1 class="page-title">{{ goalTitle }}</h1>
      <div class="header-right">
        <span v-if="tokenTotalAll !== null" class="token-agg">⨯ {{ tokenTotalAll }}</span>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading goal...</p>
    </div>

    <div v-else-if="isGoalLoaded" class="main-layout">
      <!-- Left: Goal Info and Tasks -->
      <div class="content-section">
        <div class="goal-card">
          <div class="goal-info">
            <p class="goal-desc">{{ goal.description || goal.title || 'No description' }}</p>
            <div class="progress">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: `${progressPct}%` }"></div>
              </div>
              <span class="progress-label">{{ progressPct }}%</span>
            </div>
          </div>
        </div>

        <div class="tasks-section">
          <div class="section-header">
            <h2>Tasks</h2>
            <div class="header-actions">
              <button @click="openGenerateModal" class="btn-generate btn-secondary-gen" :disabled="models.length === 0">
                <span class="icon">✨</span> Auto Generate
              </button>
            </div>
          </div>
          <div v-if="tasks.length === 0" class="empty">No tasks for this goal.</div>
          <div class="tasks-list">
            <TaskCard
              v-for="t in tasks"
              :key="t.id"
              :task="t"
              :goal-label="goalTitle"
              :token-total="tokenTotalFor(t)"
              @click="openTask(t.id)"
            />
          </div>
        </div>
      </div>

      <!-- Right: Task Generation Chat Panel -->
      <div class="chat-section">
        <UnifiedChat
          chat-type="goal"
          :target-id="goal.id"
          :model-id="chatModelId"
          :messages="goalMessages"
          :is-typing="chatLoading"
          :is-loading="chatLoading"
          :disabled="!chatModelId || !isGoalLoaded"
          :width="400"
          :show-model-selector="true"
          :available-models="models"
          @send-message="(payload) => { console.log('📨 UnifiedChat send-message:', payload); sendChat(payload) }"
          @quick-prompt="(payload) => { console.log('⚡ UnifiedChat quick-prompt:', payload); handleQuickPrompt(payload) }"
          @model-change="handleModelChange"
          @apply-all-tasks="applyAllSuggestedTasks"
          @apply-single-task="applySingleTask"
        />
      </div>
    </div>

    <!-- Error State: Goal not found -->
    <div v-else-if="goalError" class="error-container">
      <div class="error-card">
        <h2>Goal Not Found</h2>
        <p>{{ goalError }}</p>
        <p>The goal you're looking for doesn't exist or you don't have permission to access it.</p>
        <button @click="goBack" class="btn-primary">Go Back</button>
      </div>
    </div>

    <!-- AI Task Generation Modal -->
    <div v-if="showGenerateModal" class="modal-overlay" @click.self="closeGenerateModal">
      <div class="modal-content generate-modal">
        <div class="modal-header">
          <h3>Generate Tasks with AI</h3>
          <button @click="closeGenerateModal" class="btn-close">×</button>
        </div>

        <div class="modal-body">
          <!-- Model Selection -->
          <div class="form-group" v-if="!selectedModel">
            <label>Select AI Model</label>
            <select v-model="tempSelectedModel" class="form-select">
              <option value="">Choose a model...</option>
              <option v-for="m in models" :key="m.id" :value="m.id">{{ m.name }}</option>
            </select>
            <button @click="confirmModel" class="btn-primary mt-2" :disabled="!tempSelectedModel">
              Confirm Model
            </button>
          </div>

          <!-- Generation Options -->
          <div v-else-if="!generatedTasks.length && !generating">
            <div class="form-group">
              <label>Number of Tasks</label>
              <input type="number" v-model.number="maxTasks" min="1" max="20" class="form-input" />
            </div>
            <div class="form-group">
              <label>Detail Level</label>
              <select v-model="detailLevel" class="form-select">
                <option value="brief">Brief</option>
                <option value="standard">Standard</option>
                <option value="detailed">Detailed</option>
              </select>
            </div>
            <button @click="generateTasks" class="btn-primary" :disabled="generating">
              <span v-if="generating" class="spinner-sm"></span>
              Generate Tasks
            </button>
          </div>

          <!-- Generating State -->
          <div v-else-if="generating" class="generating-state">
            <div class="spinner"></div>
            <p>Generating task suggestions...</p>
          </div>

          <!-- Generated Tasks Preview -->
          <div v-else-if="generatedTasks.length" class="generated-tasks">
            <div class="gen-summary" v-if="generationSummary">
              <p>{{ generationSummary }}</p>
            </div>

            <div class="task-list">
              <div v-for="(task, idx) in generatedTasks" :key="idx" class="generated-task-item">
                <label class="task-checkbox">
                  <input type="checkbox" v-model="selectedTasks" :value="idx" />
                  <div class="task-content">
                    <div class="task-title">{{ task.title }}</div>
                    <div class="task-desc">{{ task.description }}</div>
                    <div class="task-meta">
                      <span v-if="task.suggested_type" class="task-type">{{ formatTaskType(task.suggested_type) }}</span>
                      <span v-if="task.priority" class="task-priority" :class="task.priority">{{ task.priority }}</span>
                      <span v-if="task.estimated_hours" class="task-hours">~{{ task.estimated_hours }}h</span>
                    </div>
                    <div v-if="task.validation && !task.validation.valid" class="validation-warnings">
                      <span v-for="(err, i) in task.validation.errors" :key="'e'+i" class="val-error">{{ err }}</span>
                    </div>
                    <div v-if="task.validation && task.validation.warnings?.length" class="validation-warnings">
                      <span v-for="(warn, i) in task.validation.warnings" :key="'w'+i" class="val-warning">{{ warn }}</span>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div class="gen-actions">
              <button @click="applySelectedTasks" class="btn-primary" :disabled="selectedTasks.length === 0 || applying">
                <span v-if="applying" class="spinner-sm"></span>
                Create {{ selectedTasks.length }} Task(s)
              </button>
              <button @click="regenerateTasks" class="btn-secondary">Regenerate</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.goal-detail-container { display: flex; flex-direction: column; height: 100%; overflow: hidden; }
.page-header { display: flex; align-items: center; gap: 8px; padding: 12px 16px; border-bottom: 1px solid #e9ecef; background: white; flex-shrink: 0; }
.btn-back { background: none; border: none; cursor: pointer; color: #6c757d; font-size: 18px; }
.page-title { font-size: 16px; font-weight: 600; color: #37352f; margin: 0; flex: 1; }
.header-right { display: flex; gap: 8px; align-items: center; }
.token-agg { background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 10px; padding: 2px 8px; font-size: 12px; color: #334155; }
.loading-container { padding: 24px; display: flex; flex-direction: column; align-items: center; color: #9ca3af; }
.spinner { width: 24px; height: 24px; border: 3px solid rgba(0,0,0,0.06); border-top: 3px solid #37352f; border-radius: 50%; animation: spin 0.8s linear infinite; margin-bottom: 8px; }
.spinner-sm { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,0.3); border-top: 2px solid white; border-radius: 50%; animation: spin 0.8s linear infinite; display: inline-block; margin-right: 6px; }
.spinner-xs { width: 12px; height: 12px; border: 2px solid rgba(255,255,255,0.3); border-top: 2px solid white; border-radius: 50%; animation: spin 0.8s linear infinite; display: inline-block; }
@keyframes spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }

/* Main Layout - Side by Side */
.main-layout { display: flex; flex: 1; min-height: 0; overflow: hidden; }
.content-section { flex: 1; padding: 16px; overflow-y: auto; min-width: 400px; }
.chat-section { flex: 0 0 400px; border-left: 1px solid #e9ecef; display: flex; flex-direction: column; min-height: 0; overflow: hidden; }

.goal-card { background: white; border: 1px solid #e9ecef; border-radius: 6px; padding: 12px; margin-bottom: 16px; }
.goal-desc { margin: 0 0 8px 0; color: #6c757d; }
.progress { display: flex; align-items: center; gap: 8px; }
.progress-bar { flex: 1; height: 8px; background: #e9ecef; border-radius: 4px; overflow: hidden; }
.progress-fill { height: 100%; background: linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%); }
.progress-label { font-size: 12px; color: #334155; font-weight: 600; }
.tasks-section { }
.section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.section-header h2 { margin: 0; font-size: 14px; font-weight: 600; }
.header-actions { display: flex; gap: 8px; }
.tasks-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 8px; }
.empty { color: #9ca3af; padding: 16px; text-align: center; }

/* Chat Panel Styles */
.chat-panel { display: flex; flex-direction: column; height: 100%; }
.chat-header { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; background: white; border-bottom: 1px solid #e9ecef; }
.chat-header h3 { margin: 0; font-size: 14px; font-weight: 600; color: #37352f; }
.model-select-compact select, .form-select-sm { padding: 4px 8px; font-size: 12px; border: 1px solid #d1d5db; border-radius: 4px; }
.model-badge { background: #e0e7ff; color: #3730a3; padding: 2px 8px; border-radius: 4px; font-size: 11px; }

.chat-messages { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 12px; }
.chat-welcome { text-align: center; padding: 32px 16px; color: #6b7280; }
.welcome-icon { font-size: 48px; margin-bottom: 12px; }
.welcome-title { font-size: 16px; font-weight: 600; color: #374151; margin: 0 0 8px 0; }
.welcome-text { font-size: 13px; margin: 0 0 16px 0; line-height: 1.5; }
.quick-prompts { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; }
.quick-prompt { background: white; border: 1px solid #d1d5db; border-radius: 16px; padding: 6px 12px; font-size: 12px; cursor: pointer; transition: all 0.15s; }
.quick-prompt:hover:not(:disabled) { background: #f3f4f6; border-color: #9ca3af; }
.quick-prompt:disabled { opacity: 0.5; cursor: not-allowed; }

.chat-message { max-width: 85%; padding: 10px 14px; border-radius: 12px; }
.chat-message.user { align-self: flex-end; background: #3b82f6; color: white; }
.chat-message.assistant { align-self: flex-start; background: white; border: 1px solid #e5e7eb; color: #374151; }
.chat-message.thinking { background: #f3f4f6; }
.thinking-dots { display: flex; gap: 4px; padding: 4px 0; }
.thinking-dots span { width: 6px; height: 6px; background: #9ca3af; border-radius: 50%; animation: bounce 1.4s infinite ease-in-out both; }
.thinking-dots span:nth-child(1) { animation-delay: -0.32s; }
.thinking-dots span:nth-child(2) { animation-delay: -0.16s; }
@keyframes bounce { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1); } }

.message-content { font-size: 13px; line-height: 1.5; }
.message-content p { margin: 0 0 8px 0; }
.message-content p:last-child { margin-bottom: 0; }
.message-content ul, .message-content ol { margin: 8px 0; padding-left: 20px; }
.message-content li { margin: 4px 0; }

/* Suggested Tasks in Chat */
.suggested-tasks-inline { margin-top: 12px; padding-top: 12px; border-top: 1px solid #e5e7eb; }
.suggested-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; font-size: 12px; font-weight: 500; color: #6b7280; }
.btn-apply-all { background: #10b981; color: white; border: none; padding: 4px 10px; border-radius: 4px; font-size: 11px; cursor: pointer; }
.btn-apply-all:hover:not(:disabled) { background: #059669; }
.btn-apply-all:disabled { opacity: 0.5; cursor: not-allowed; }
.suggested-task-item { display: flex; justify-content: space-between; align-items: center; padding: 8px; background: #f9fafb; border-radius: 6px; margin-bottom: 6px; }
.task-info { flex: 1; min-width: 0; }
.task-title { font-size: 12px; font-weight: 500; color: #374151; display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.task-type-badge { background: #e0e7ff; color: #3730a3; padding: 1px 6px; border-radius: 3px; font-size: 10px; margin-left: 6px; }
.btn-apply-one { background: #3b82f6; color: white; border: none; width: 24px; height: 24px; border-radius: 4px; font-size: 14px; cursor: pointer; flex-shrink: 0; margin-left: 8px; }
.btn-apply-one:hover:not(:disabled) { background: #2563eb; }
.btn-apply-one:disabled { opacity: 0.5; cursor: not-allowed; }

/* Chat Input */
.chat-input-area { display: flex; gap: 8px; padding: 12px 16px; background: white; border-top: 1px solid #e9ecef; }
.chat-input-area textarea { flex: 1; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 13px; resize: none; font-family: inherit; }
.chat-input-area textarea:focus { outline: none; border-color: #3b82f6; }
.chat-input-area textarea:disabled { background: #f3f4f6; }
.btn-send { width: 40px; height: 40px; background: #3b82f6; color: white; border: none; border-radius: 8px; font-size: 18px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.btn-send:hover:not(:disabled) { background: #2563eb; }
.btn-send:disabled { background: #93c5fd; cursor: not-allowed; }

.btn-secondary-gen { background: #f3f4f6; color: #374151; border: 1px solid #d1d5db; }
.btn-secondary-gen:hover:not(:disabled) { background: #e5e7eb; }

@media (max-width: 1024px) {
  .main-layout { flex-direction: column; }
  .content-section { flex: none; width: 100%; min-width: auto; }
  .chat-section { flex: 0 0 400px; width: 100%; border-left: none; border-top: 1px solid #e9ecef; }
}

/* Generate Button */
.btn-generate {
  display: flex; align-items: center; gap: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white; border: none; border-radius: 6px;
  padding: 6px 12px; font-size: 12px; font-weight: 500;
  cursor: pointer; transition: opacity 0.2s;
}
.btn-generate:hover { opacity: 0.9; }
.btn-generate:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-generate .icon { font-size: 14px; }

/* Modal */
.modal-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5); display: flex;
  align-items: center; justify-content: center; z-index: 1000;
}
.modal-content {
  background: white; border-radius: 12px;
  max-width: 600px; width: 90%; max-height: 80vh;
  display: flex; flex-direction: column;
}
.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px; border-bottom: 1px solid #e9ecef;
}
.modal-header h3 { margin: 0; font-size: 16px; }
.btn-close { background: none; border: none; font-size: 24px; cursor: pointer; color: #6c757d; }
.modal-body { padding: 16px; overflow-y: auto; }

/* Form Elements */
.form-group { margin-bottom: 16px; }
.form-group label { display: block; font-size: 12px; font-weight: 500; color: #374151; margin-bottom: 4px; }
.form-input, .form-select {
  width: 100%; padding: 8px 12px; border: 1px solid #d1d5db;
  border-radius: 6px; font-size: 14px;
}
.btn-primary {
  background: #3b82f6; color: white; border: none;
  padding: 10px 20px; border-radius: 6px; font-weight: 500;
  cursor: pointer; display: inline-flex; align-items: center;
}
.btn-primary:hover { background: #2563eb; }
.btn-primary:disabled { background: #93c5fd; cursor: not-allowed; }
.btn-secondary {
  background: #f3f4f6; color: #374151; border: 1px solid #d1d5db;
  padding: 10px 20px; border-radius: 6px; font-weight: 500; cursor: pointer;
}
.btn-secondary:hover { background: #e5e7eb; }
.mt-2 { margin-top: 8px; }

/* Generating State */
.generating-state { text-align: center; padding: 32px; }
.generating-state p { color: #6b7280; margin-top: 8px; }

/* Generated Tasks */
.generated-tasks { }
.gen-summary { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 6px; padding: 12px; margin-bottom: 16px; }
.gen-summary p { margin: 0; font-size: 13px; color: #166534; }

.task-list { display: flex; flex-direction: column; gap: 8px; max-height: 400px; overflow-y: auto; }
.generated-task-item { border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
.task-checkbox { display: flex; gap: 12px; padding: 12px; cursor: pointer; }
.task-checkbox input[type="checkbox"] { margin-top: 4px; }
.task-content { flex: 1; }
.task-title { font-weight: 500; color: #111827; margin-bottom: 4px; }
.task-desc { font-size: 13px; color: #6b7280; margin-bottom: 8px; }
.task-meta { display: flex; gap: 8px; flex-wrap: wrap; }
.task-type { background: #e0e7ff; color: #3730a3; padding: 2px 8px; border-radius: 4px; font-size: 11px; }
.task-priority { padding: 2px 8px; border-radius: 4px; font-size: 11px; text-transform: capitalize; }
.task-priority.high { background: #fee2e2; color: #dc2626; }
.task-priority.medium { background: #fef3c7; color: #d97706; }
.task-priority.low { background: #d1fae5; color: #059669; }
.task-hours { background: #f3f4f6; color: #4b5563; padding: 2px 8px; border-radius: 4px; font-size: 11px; }

.validation-warnings { margin-top: 8px; display: flex; flex-wrap: wrap; gap: 4px; }
.val-error { background: #fee2e2; color: #dc2626; padding: 2px 6px; border-radius: 4px; font-size: 11px; }
.val-warning { background: #fef3c7; color: #d97706; padding: 2px 6px; border-radius: 4px; font-size: 11px; }

.gen-actions { display: flex; gap: 8px; margin-top: 16px; padding-top: 16px; border-top: 1px solid #e5e7eb; }
</style>

<script>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { goalsApi, tasksApi, llmApi, chatApi } from '@/services/api'
import TaskCard from '@/components/workstream/TaskCard.vue'
import UnifiedChat from '@/components/UnifiedChat.vue'

export default {
  name: 'GoalDetailView',
  components: { TaskCard, UnifiedChat },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const id = computed(() => route.params.id)
    const loading = ref(false)
    const goal = ref({})
    const progress = ref(null)
    const tasks = ref([])
    const workstreamId = computed(() => route.query.ws || route.query.workstream_id || route.query.workstream || goal.value?.workstream_id)

    // Computed properties for store integration
    const goalMessages = computed(() =>
      store.getters['chat/getMessages']('goal', id.value)
    )

    const isGoalLoaded = computed(() => {
      const result = goal.value && goal.value.id && !loading.value
      console.log('🔍 isGoalLoaded computed:', result, {
        goal: goal.value,
        goalId: goal.value?.id,
        loading: loading.value
      })
      return result
    })

    const goalError = computed(() => {
      const result = !loading.value && (!goal.value || !goal.value.id) ? 'Goal not found or failed to load' : null
      console.log('🔍 goalError computed:', result, {
        loading: loading.value,
        goal: goal.value,
        goalId: goal.value?.id
      })
      return result
    })

    // AI Task Generation State (Modal-based)
    const showGenerateModal = ref(false)
    const models = ref([])
    const selectedModel = ref('')
    const tempSelectedModel = ref('')
    const generating = ref(false)
    const applying = ref(false)
    const generatedTasks = ref([])
    const selectedTasks = ref([])
    const generationSummary = ref('')
    const maxTasks = ref(10)
    const detailLevel = ref('standard')

    // Chat-based Task Generation State
    const chatMessagesRef = ref(null)
    const chatModelId = ref('')
    const chatInput = ref('')
    const chatLoading = ref(false)
    const chatHistory = ref([]) // Array of { role, content, suggested_tasks? }

    const goalTitle = computed(() => goal.value?.description || goal.value?.title || goal.value?.name || id.value)
    const progressPct = computed(() => {
      if (typeof progress.value?.percent === 'number') return Math.round(progress.value.percent)
      if (typeof goal.value?.progress === 'number') return Math.round(goal.value.progress)
      return 0
    })

    const tokenTotalFor = (t) => {
      const r = (t?.token_allocated !== undefined) ? t.token_allocated : t?.token_reward
      if (!r) return null
      if (typeof r === 'number') return r
      if (typeof r === 'object') {
        if (typeof r.total === 'number') return r.total
        return Object.values(r).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
      }
      return null
    }

    const tokenTotalAll = computed(() => {
      const vals = tasks.value.map(tokenTotalFor).filter(v => typeof v === 'number')
      if (vals.length === 0) return null
      return vals.reduce((a,b) => a + b, 0)
    })

    const loadModels = async () => {
      try {
        const res = await llmApi.getModels()
        models.value = Array.isArray(res) ? res : (res?.models || [])
        // Auto-select first model if available
        if (models.value.length > 0 && !selectedModel.value) {
          tempSelectedModel.value = models.value[0].id
        }
      } catch (e) {
        console.warn('Failed to load models', e)
      }
    }

    const refresh = async () => {
      console.log('🔄 Starting goal refresh, current state:', {
        id: id.value,
        loading: loading.value,
        goal: goal.value,
        workstreamId: workstreamId.value
      })

      loading.value = true
      try {
        console.log('📡 Loading goal with ID:', id.value)
        if (workstreamId.value) {
          console.log('🏢 Trying workstream-scoped goal fetch for workstream:', workstreamId.value)
          try {
            goal.value = await goalsApi.getInWorkstream(workstreamId.value, id.value)
            console.log('✅ Goal loaded from workstream:', goal.value)
            progress.value = null
          } catch (wsError) {
            console.warn('⚠️ Workstream-scoped goal fetch failed:', wsError)
            console.log('🌐 Trying global goal fetch')
            try {
              goal.value = await goalsApi.getById(id.value)
              console.log('✅ Goal loaded globally:', goal.value)
              try { progress.value = await goalsApi.progress(id.value) } catch (_) {}
            } catch (directError) {
              console.error('❌ Goal not found globally:', id.value, directError)
              goal.value = null
              console.log('🚫 Setting goal to null due to not found error')
              // Don't throw here, let UI handle the error state
            }
          }
        } else {
          console.log('🌐 Trying direct goal fetch (no workstream context)')
          try {
            goal.value = await goalsApi.getById(id.value)
            console.log('✅ Goal loaded directly:', goal.value)
            try { progress.value = await goalsApi.progress(id.value) } catch (_) {}
          } catch (directError) {
            console.error('❌ Goal not found directly:', id.value, directError)
            goal.value = null
            console.log('🚫 Setting goal to null due to not found error')
            // Don't throw here, let UI handle the error state
          }
        }

        if (goal.value && goal.value.id) {
          console.log('📋 Loading tasks for goal:', goal.value.id)
          const res = await tasksApi.getAll({ goal_id: goal.value.id })
          tasks.value = Array.isArray(res) ? res : (res?.tasks || [])
          console.log('✅ Goal and tasks loaded successfully, tasks count:', tasks.value.length)
        } else {
          console.log('🚫 Skipping task load due to invalid goal data')
        }
      } catch (error) {
        console.error('💥 Unexpected error in refresh:', error)
        goal.value = null // Ensure error state
      } finally {
        loading.value = false
        console.log('🏁 Goal refresh completed, final state:', {
          loading: loading.value,
          goal: goal.value,
          isGoalLoaded: goal.value && goal.value.id && !loading.value,
          goalError: !loading.value && (!goal.value || !goal.value.id)
        })
      }
    }

    const openTask = (taskId) => {
      // Pass workstream_id as query parameter for back navigation
      const workstreamId = goal.value?.workstream_id
      router.push({
        name: 'task-detail',
        params: { id: taskId },
        query: workstreamId ? { workstream_id: workstreamId } : {}
      })
    }

    const goBack = () => {
      const wsId = goal.value?.workstream_id
      if (wsId) router.push({ name: 'workstream-detail', params: { id: wsId } })
      else router.back()
    }

    // AI Task Generation Methods
    const openGenerateModal = () => {
      showGenerateModal.value = true
      generatedTasks.value = []
      selectedTasks.value = []
      generationSummary.value = ''
    }

    const closeGenerateModal = () => {
      showGenerateModal.value = false
    }

    const confirmModel = () => {
      selectedModel.value = tempSelectedModel.value
    }

    const generateTasks = async () => {
      if (!selectedModel.value) return
      generating.value = true
      generatedTasks.value = []
      selectedTasks.value = []

      try {
        const result = await tasksApi.generateForGoal(id.value, {
          goal_title: goal.value.title || goal.value.description,
          goal_description: goal.value.description || '',
          model_id: selectedModel.value,
          max_tasks: maxTasks.value,
          include_types: true,
          detail_level: detailLevel.value
        })

        generatedTasks.value = result.tasks || []
        generationSummary.value = result.summary || ''
        // Select all valid tasks by default
        selectedTasks.value = generatedTasks.value
          .map((t, i) => ({ t, i }))
          .filter(({ t }) => !t.validation || t.validation.valid)
          .map(({ i }) => i)
      } catch (e) {
        console.error('Task generation failed:', e)
        alert('Failed to generate tasks. Please try again.')
      } finally {
        generating.value = false
      }
    }

    const regenerateTasks = () => {
      generateTasks()
    }

    const applySelectedTasks = async () => {
      if (selectedTasks.value.length === 0) return
      const wsId = workstreamId.value
      if (!wsId) {
        alert('Workstream context is required to create tasks.')
        return
      }

      applying.value = true
      try {
        const tasksToCreate = selectedTasks.value.map(idx => {
          const task = generatedTasks.value[idx]
          return {
            ...task,
            token_allocated: task.token_allocated || task.token_reward || 100
          }
        })
        await tasksApi.applyGeneratedTasks(id.value, wsId, tasksToCreate)
        closeGenerateModal()
        await refresh() // Reload tasks
      } catch (e) {
        console.error('Failed to create tasks:', e)
        alert('Failed to create some tasks. Please try again.')
      } finally {
        applying.value = false
      }
    }

    const formatTaskType = (type) => {
      if (!type) return ''
      return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    }

    // ---- Chat-based Task Generation Methods ----
    const scrollChatToBottom = () => {
      nextTick(() => {
        if (chatMessagesRef.value) {
          chatMessagesRef.value.scrollTop = chatMessagesRef.value.scrollHeight
        }
      })
    }

    const getModelName = (modelId) => {
      const m = models.value.find(x => x.id === modelId)
      return m?.name || modelId
    }

    const renderMarkdown = (text) => {
      if (!text) return ''
      // Simple markdown rendering
      let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      // Bold
      html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      // Italic
      html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>')
      // Lists
      html = html.replace(/^- (.+)$/gm, '<li>$1</li>')
      html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
      // Paragraphs
      html = html.split(/\n\n+/).map(p => p.trim() ? `<p>${p}</p>` : '').join('')
      return html
    }

    // Goal chat with special task generation capabilities
    const sendChat = async (payload) => {
      console.log('🔥 sendChat called with payload:', payload)
      console.log('🔥 Current goal state:', goal.value)
      console.log('🔥 Current id:', id.value)
      console.log('🔥 isGoalLoaded:', isGoalLoaded.value)
      console.log('🔥 goalError:', goalError.value)

      // Handle both direct messages and quick prompt objects
      let message
      if (typeof payload === 'string') {
        message = payload
      } else if (payload.message) {
        message = payload.message
      } else if (payload.label || payload.text) {
        // Handle quick prompt objects
        message = payload.label || payload.text
      } else {
        message = String(payload)
      }

      const modelId = payload.modelId || chatModelId.value

      console.log('🔥 Processed message:', message)
      console.log('🔥 Processed modelId:', modelId)

      if (!message) {
        console.log('❌ No message provided, returning')
        return
      }

      // Validate that goal data is loaded
      if (!goal.value || !goal.value.id) {
        console.log('❌ Goal data not loaded:', { goal: goal.value, goalId: goal.value?.id })
        store.commit('chat/ADD_MESSAGE', {
          target: 'goal',
          id: id.value,
          message: {
            role: 'assistant',
            content: 'Loading goal information. Please wait and try again.'
          }
        })
        return
      }

      console.log('✅ Goal validation passed, goal:', goal.value)

      // Validate authentication
      const token = localStorage.getItem('token')
      if (!token) {
        console.log('❌ No authentication token found')
        store.commit('chat/ADD_MESSAGE', {
          target: 'goal',
          id: id.value,
          message: {
            role: 'assistant',
            content: 'You must be logged in to use chat functionality. Please log in and try again.'
          }
        })
        return
      }

      console.log('✅ Authentication validation passed')

      // Validate model selection
      if (!modelId) {
        console.log('❌ No model selected')
        store.commit('chat/ADD_MESSAGE', {
          target: 'goal',
          id: id.value,
          message: {
            role: 'assistant',
            content: 'Please select a model before sending messages.'
          }
        })
        return
      }

      console.log('✅ Model validation passed, proceeding with API call')

      try {
        chatLoading.value = true

        // Add user message to store
        store.commit('chat/ADD_MESSAGE', {
          target: 'goal',
          id: id.value,
          message: { role: 'user', content: message, timestamp: new Date() }
        })

        // Start thinking indicator
        store.commit('chat/START_THINKING', { target: 'goal', id: id.value })

        // Use the special goal chat API that supports task generation
        const chatOptions = {
          goal_title: goal.value.title || goal.value.description,
          goal_description: goal.value.description || '',
          model_id: modelId,
          history: [] // TODO: Add conversation history support
        }

        console.log('🎯 Calling chatGoal API with:', {
          goalId: goal.value.id,
          message: message,
          options: chatOptions,
          goalData: goal.value
        })

        const response = await tasksApi.chatGoal(goal.value.id, message, chatOptions)

        // Update with AI response including suggested tasks
        const reply = response.message || 'OK'
        const messageData = {
          role: 'assistant',
          content: reply
        }

        if (response.suggested_tasks && Array.isArray(response.suggested_tasks)) {
          messageData.suggested_tasks = response.suggested_tasks
        }

        store.commit('chat/RESOLVE_THINKING', {
          target: 'goal',
          id: id.value,
          content: messageData.content,
          suggested_tasks: messageData.suggested_tasks
        })

      } catch (err) {
        console.error('Error sending goal chat message:', err)
        console.error('Full error response:', err.response?.data)
        console.error('Error status:', err.response?.status)

        let errorContent = 'Sorry, I encountered an error. Please try again.'

        if (err.response) {
          const status = err.response.status
          const errorData = err.response.data

          console.log('Backend error details:', { status, errorData })

          if (status === 401) {
            errorContent = 'Authentication required. Please log in and try again.'
          } else if (status === 403) {
            errorContent = 'You do not have permission to perform this action.'
          } else if (status === 400) {
            if (errorData?.error) {
              errorContent = `Request error: ${errorData.error}`
            } else {
              errorContent = 'Invalid request. Please check your message and try again.'
            }
          } else if (status === 500) {
            errorContent = 'Server error. Please try again later.'
          } else if (errorData?.message) {
            errorContent = `Error: ${errorData.message}`
          }
        } else if (err.message) {
          errorContent = `Connection error: ${err.message}`
        }

        store.commit('chat/RESOLVE_THINKING', {
          target: 'goal',
          id: id.value,
          content: errorContent
        })
      } finally {
        chatLoading.value = false
      }
    }

    const handleQuickPrompt = (payload) => {
      sendChat(payload)
    }

    const applyAllSuggestedTasks = async (suggestedTasks) => {
      console.log('🔧 applyAllSuggestedTasks called with:', suggestedTasks)

      if (!suggestedTasks || suggestedTasks.length === 0) {
        console.log('❌ No suggested tasks provided')
        return
      }

      const wsId = workstreamId.value
      if (!wsId) {
        console.log('❌ No workstream context')
        alert('Workstream context is required to create tasks.')
        return
      }

      const tasksWithAllocatedTokens = suggestedTasks.map(task => ({
        ...task,
        token_allocated: task.token_allocated || task.token_reward || 100
      }))

      console.log('✅ Creating tasks:', { goalId: id.value, wsId, taskCount: tasksWithAllocatedTokens.length })
      applying.value = true

      try {
        await tasksApi.applyGeneratedTasks(id.value, wsId, tasksWithAllocatedTokens)
        console.log('✅ Tasks created successfully')
        await refresh()

        // Add confirmation to store-based chat
        store.commit('chat/ADD_MESSAGE', {
          target: 'goal',
          id: id.value,
          message: {
            role: 'assistant',
            content: `Created ${suggestedTasks.length} task(s) successfully.`
          }
        })
      } catch (e) {
        console.error('❌ Failed to create tasks:', e)
        alert('Failed to create tasks. Please try again.')
      } finally {
        applying.value = false
      }
    }

    const applySingleTask = async (task) => {
      const wsId = workstreamId.value
      if (!wsId) {
        alert('Workstream context is required to create tasks.')
        return
      }

      const taskWithAllocatedTokens = {
        ...task,
        token_allocated: task.token_allocated || task.token_reward || 100
      }

      applying.value = true
      try {
        await tasksApi.applyGeneratedTasks(id.value, wsId, [taskWithAllocatedTokens])
        await refresh()
        // Add confirmation to store-based chat
        store.commit('chat/ADD_MESSAGE', {
          target: 'goal',
          id: id.value,
          message: {
            role: 'assistant',
            content: `Created task: "${task.title}"`
          }
        })
      } catch (e) {
        console.error('Failed to create task:', e)
        alert('Failed to create task. Please try again.')
      } finally {
        applying.value = false
      }
    }

    const handleModelChange = (modelId) => {
      chatModelId.value = modelId
    }

    // Auto-select first model for chat when models load
    watch(models, (newModels) => {
      if (newModels.length > 0 && !chatModelId.value) {
        chatModelId.value = newModels[0].id
      }
    })

    onMounted(() => {
      console.log('🎬 GoalDetailView mounted with route params:', route.params)
      console.log('🎬 Initial state:', {
        id: id.value,
        loading: loading.value,
        goal: goal.value,
        workstreamId: workstreamId.value
      })
      refresh()
      loadModels()
    })

    return {
      id, loading, goal, tasks, goalTitle, progressPct, tokenTotalFor, tokenTotalAll,
      isGoalLoaded, goalError,
      openTask, goBack,
      // AI Generation (Modal)
      showGenerateModal, models, selectedModel, tempSelectedModel,
      generating, applying, generatedTasks, selectedTasks, generationSummary,
      maxTasks, detailLevel,
      openGenerateModal, closeGenerateModal, confirmModel,
      generateTasks, regenerateTasks, applySelectedTasks, formatTaskType,
       // Chat-based Generation
       chatMessagesRef, chatModelId, chatInput, chatLoading, goalMessages,
       getModelName, renderMarkdown, sendChat, handleQuickPrompt,
       applyAllSuggestedTasks, applySingleTask, handleModelChange
    }
  }
}
</script>
